import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import DnaRadar from "../components/DnaRadar";
import { compareGins, listGins } from "../lib/api";
import { getCategoryColor } from "../lib/constants";
import { recordEvent } from "../lib/adminApi";

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allGins, setAllGins] = useState([]);
  const [ids, setIds] = useState(() => {
    const fromUrl = (searchParams.get("ids") || "").split(",").filter(Boolean);
    if (fromUrl.length) return fromUrl;
    try { return JSON.parse(localStorage.getItem("beno-compare") || "[]"); } catch { return []; }
  });
  const [data, setData] = useState(null);
  const [picker, setPicker] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { listGins().then((d) => setAllGins(d.gins)); }, []);

  useEffect(() => {
    setSearchParams(ids.length ? { ids: ids.join(",") } : {});
    localStorage.setItem("beno-compare", JSON.stringify(ids));
    if (ids.length >= 2) {
      compareGins(ids).then(setData);
      recordEvent("compare", { extra: { gin_ids: ids } });
    } else {
      setData(null);
    }
  }, [ids, setSearchParams]);

  const addGin = (id) => { setIds((arr) => arr.length >= 3 ? arr : [...arr, id]); setPicker(false); };
  const removeGin = (id) => setIds((arr) => arr.filter((i) => i !== id));

  const availableForPick = useMemo(
    () => allGins.filter((g) => !ids.includes(g.id) &&
      (search.trim() === "" || g.name.toLowerCase().includes(search.toLowerCase()) ||
       g.country.toLowerCase().includes(search.toLowerCase()))),
    [allGins, ids, search]
  );

  const radarItems = (data?.gins || []).map((g) => ({
    name: g.name, dna: g.dna, color: getCategoryColor(g.category), category: g.category,
  }));

  // Empty / underfilled state
  if (ids.length < 2) {
    return (
      <div className="px-5 md:px-12 max-w-7xl mx-auto py-12 min-h-[70vh]" data-testid="compare-page">
        <Link to="/gin/explore" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors mb-6" data-testid="back-explore-compare">
          <ArrowLeft size={14} /> Back to Atlas
        </Link>
        <p className="overline text-[#C17F59] mb-2">Compare</p>
        <h1 className="font-display text-5xl tracking-tight mb-3">Two or three gins, side by side.</h1>
        <p className="text-white/65 max-w-xl mb-10">Stack their DNA, see which is most floral, most coastal, most Indian.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
          {[0, 1, 2].map((slot) => {
            const id = ids[slot];
            const gin = id ? allGins.find((g) => g.id === id) : null;
            return (
              <div key={slot} className="bg-[#15171A] border border-dashed border-white/15 aspect-[3/4] flex flex-col items-center justify-center p-6 relative" data-testid={`compare-slot-${slot}`}>
                {gin ? (
                  <>
                    <BottleSilhouette category={gin.category} size={90} />
                    <p className="font-display text-2xl mt-3 text-center">{gin.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{gin.country} · ₹{gin.price}</p>
                    <button onClick={() => removeGin(gin.id)} className="absolute top-2 right-2 text-white/40 hover:text-white" data-testid={`remove-${gin.id}`}>
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <button onClick={() => setPicker(true)} className="text-white/40 hover:text-[#C17F59] transition-colors flex flex-col items-center gap-2" data-testid={`add-slot-${slot}`}>
                    <Plus size={28} strokeWidth={1.2} />
                    <span className="text-xs uppercase tracking-[0.25em]">Add a gin</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {picker && <PickerModal options={availableForPick} search={search} setSearch={setSearch} onPick={addGin} onClose={() => setPicker(false)} />}
      </div>
    );
  }

  // Full compare
  return (
    <div className="px-5 md:px-12 max-w-7xl mx-auto py-10 pb-24" data-testid="compare-page">
      <Link to="/gin/explore" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors mb-6">
        <ArrowLeft size={14} /> Back to Atlas
      </Link>
      <p className="overline text-[#C17F59] mb-2">Compare</p>
      <h1 className="font-display text-5xl tracking-tight mb-10">DNA, side by side.</h1>

      {/* Unavailable warning */}
      {(data?.gins || []).some((g) => g.availability_status === "NOT_AVAILABLE" || g.is_available === false) && (
        <div className="bg-[#15171A] border border-[#C17F59]/30 px-5 py-3 mb-6" data-testid="compare-unavailable-warning">
          <p className="text-xs uppercase tracking-[0.22em] text-[#C17F59] mb-1">Heads-up</p>
          <p className="text-sm text-white/75">
            {(data?.gins || []).filter((g) => g.availability_status === "NOT_AVAILABLE" || g.is_available === false)
              .map((g) => g.name).join(" · ")} not on tonight's menu. You can still compare DNA, but the bar can't pour it tonight.
          </p>
        </div>
      )}

      {/* Gin headers */}
      <div className={`grid grid-cols-${ids.length} gap-4 mb-6`}
           style={{ gridTemplateColumns: `repeat(${ids.length}, minmax(0, 1fr))` }}>
        {data?.gins.map((g, idx) => (
          <motion.div
            key={g.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-[#15171A] border border-white/8 p-5 relative"
            data-testid={`compare-card-${g.id}`}
          >
            <button onClick={() => removeGin(g.id)} className="absolute top-2 right-2 text-white/40 hover:text-white" data-testid={`compare-remove-${g.id}`}>
              <X size={14} />
            </button>
            <div className="flex flex-col items-center text-center">
              <BottleSilhouette category={g.category} size={72} />
              <Link to={`/gin/${g.id}`}><h3 className="font-display text-2xl mt-3 hover:text-[#C17F59] transition-colors">{g.name}</h3></Link>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{g.country} · ₹{g.price} · {g.abv}%</p>
              <p className="text-[10px] uppercase tracking-[0.2em] mt-2" style={{ color: getCategoryColor(g.category) }}>
                {g.styles.slice(0, 2).join(" · ")}
              </p>
            </div>
          </motion.div>
        ))}
        {ids.length < 3 && (
          <button onClick={() => setPicker(true)}
                  className="bg-transparent border border-dashed border-white/15 hover:border-[#C17F59] hover:text-[#C17F59] flex items-center justify-center text-white/40 transition-colors"
                  data-testid="compare-add-more">
            <Plus size={22} />
          </button>
        )}
      </div>

      {/* Radar overlay */}
      <div className="bg-[#15171A] border border-white/8 p-6 mb-10" data-testid="compare-radar">
        <p className="overline text-[#C17F59] mb-3">BENO Gin DNA · Overlay</p>
        <DnaRadar items={radarItems} height={420} showLegend />
        <div className="flex flex-wrap gap-4 justify-center pt-3">
          {radarItems.map((it) => (
            <span key={it.name} className="flex items-center gap-2 text-xs text-white/70">
              <span className="w-3 h-3" style={{ background: it.color }} />
              {it.name}
            </span>
          ))}
        </div>
      </div>

      {/* TASTE THE DIFFERENCE — convert compare to a custom flight */}
      {data?.gins && data.gins.length >= 2 && (() => {
        const anyUnavail = data.gins.some((g) => g.availability_status === "NOT_AVAILABLE" || g.is_available === false);
        return (
        <section className="my-10 relative bg-[#101113] border border-[#C17F59]/30 p-8 md:p-12 overflow-hidden" data-testid="taste-the-difference">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(193,127,89,0.18), transparent 70%)" }} />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <p className="overline text-[#C17F59] mb-3">Taste The Difference</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mb-3">
                Don't argue about it. <em className="not-italic text-[#C17F59]">Pour all three.</em>
              </h2>
              <p className="text-white/65 max-w-xl mb-1 leading-relaxed">
                Build this comparison as a tasting flight — three small pours, one guided journey.
                We'll line them up in tasting order so the arc climbs.
              </p>
              <p className="font-hand text-xl text-[#D89A74] -rotate-1 mt-3">— the cheapest way to settle a gin debate.</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              {anyUnavail ? (
                <div
                  className="inline-flex flex-col items-start md:items-end gap-1 border border-white/10 px-5 py-3 text-[11px] uppercase tracking-[0.25em] text-white/50"
                  data-testid="flight-disabled-unavailable"
                >
                  <span>Flight unavailable</span>
                  <span className="normal-case tracking-normal text-[10px] text-white/40 max-w-[220px] text-left md:text-right">
                    One of these gins isn't on tonight's menu.
                  </span>
                </div>
              ) : (
                <Link to={`/gin/flights/custom?ids=${ids.join(",")}`}
                      className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-6 py-4 text-xs uppercase tracking-[0.3em] hover:bg-[#D89A74] transition-colors"
                      data-testid="taste-the-difference-cta">
                  Pour as a flight
                  <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>
        </section>
        );
      })()}

      {/* Insights */}
      {data?.insights && (
        <div className="bg-[#15171A] border border-white/8 p-6 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6" data-testid="compare-insights">
          <Insight label="Most Classic" ginId={data.insights.classic} gins={data.gins} />
          <Insight label="Most Citrus" ginId={data.insights.citrus} gins={data.gins} />
          <Insight label="Most Floral" ginId={data.insights.floral} gins={data.gins} />
          <Insight label="Most Spicy" ginId={data.insights.spicy} gins={data.gins} />
          <Insight label="Most Coastal" ginId={data.insights.coastal} gins={data.gins} />
          <Insight label="Most Complex" ginId={data.insights.complex} gins={data.gins} />
          <Insight label="Best Value" ginId={data.insights.value} gins={data.gins} />
          <Insight label="Most Premium" ginId={data.insights.premium} gins={data.gins} />
        </div>
      )}

      {picker && <PickerModal options={availableForPick} search={search} setSearch={setSearch} onPick={addGin} onClose={() => setPicker(false)} />}
    </div>
  );
}

function Insight({ label, ginId, gins }) {
  const g = gins.find((x) => x.id === ginId);
  if (!g) return null;
  return (
    <div data-testid={`insight-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <p className="overline text-white/40 mb-1">{label}</p>
      <Link to={`/gin/${g.id}`} className="font-display text-xl hover:text-[#C17F59] transition-colors">{g.name}</Link>
    </div>
  );
}

function PickerModal({ options, search, setSearch, onPick, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md px-5 py-10 overflow-y-auto"
                data-testid="picker-modal">
      <div className="max-w-3xl mx-auto bg-[#15171A] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="overline text-[#C17F59]">Pick a Gin</p>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="picker-close">
            <X size={18} />
          </button>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search gins..."
          className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-3 mb-5 text-sm focus:outline-none transition-colors"
          autoFocus
          data-testid="picker-search"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
          {options.slice(0, 80).map((g) => (
            <button key={g.id} onClick={() => onPick(g.id)}
                    className="flex items-center gap-3 p-3 border border-white/5 hover:border-[#C17F59]/50 text-left transition-colors"
                    data-testid={`picker-pick-${g.id}`}>
              <BottleSilhouette category={g.category} size={32} />
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg truncate">{g.name}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{g.country} · ₹{g.price}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
